import { Button } from "@mui/material";
import { isEmpty } from "lodash-es";
import type { FC } from "react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import type { HandleSaveMoment } from "../../../moment/component/buttons/hooks/types";
import { useHandleSave } from "../../../moment/component/buttons/hooks/useHandleSave";
import { FollowUpSendTestDialog } from "./follow-up-send-test-dialog";
import type { FollowUpButtonsProps } from "./types";

export const FollowUpSentTestButton: FC<
  Pick<
    FollowUpButtonsProps,
    "uiData" | "saveData" | "errors" | "saving" | "onSaving" | "onSaved"
  > & {
    disabled: boolean;
  }
> = ({ uiData, saveData, errors, saving, onSaving, onSaved, disabled }) => {
  const navigate = useNavigate();
  const dialog = useDialog<HandleSaveMoment>();

  const { callback: save } = useHandleSave({
    reset: () => {},
    validate: () => Promise.resolve(isEmpty(errors.errors)),
  });

  const { handleOpen } = dialog;

  const handleSave = useCallback(() => {
    onSaving();

    void save(saveData)
      .then(() => {
        onSaved(null);
        if (saveData.follow_up?.id) {
          void navigate(`/follow-up/${saveData.follow_up?.id}`, { replace: true });
        }
        toast.success("Follow-Up saved successfully.");
        handleOpen();
      })
      .catch((err: Error) => {
        onSaved(err.message);
      });
  }, [saveData, save, onSaving, onSaved, navigate, handleOpen]);

  if (!uiData.channel || !saveData.id) return null;

  return (
    <>
      <Button
        data-analytics-id="follow-up-send-test"
        sx={{ ml: 2 }}
        loading={saving}
        disabled={disabled}
        onClick={handleSave}
        id="sendTestButton"
        variant="outlined"
      >
        Send Test
      </Button>
      <FollowUpSendTestDialog
        id={saveData.id}
        channel={uiData.channel}
        open={dialog.open}
        onCancel={dialog.handleClose}
        setTestDialog={(val: boolean) => {
          if (!val) dialog.handleClose();
          else dialog.handleOpen();
        }}
        saving={saving}
        setSaving={(val: boolean) => {
          if (val) onSaving();
          else onSaved(null);
        }}
      />
    </>
  );
};
